/**
 * The closed set of send-failure CAUSES an engine can observe (#371), and the one
 * mapping from a cause to the structured `SendFailure` record (#333 §1.4).
 *
 * Today WA Web's own send collapses four different failures into `ERROR_UNKNOWN`
 * (server nack, oversized payload, invalid protobuf, any other throw). The
 * extension's observe-only hooks into WA Web's send job and media upload capture
 * which one it was; the Baileys pod can report the same reasons from its own
 * throw sites. The reason names are engine-neutral.
 *
 * Pure and dependency-free (Node + browser). Tokens carry codes, statuses and
 * class names only, never content.
 */

import type { EngineErrorCode } from '../interfaces/whatsapp-tools-commands.interface';
import type { SendFailEngine, SendFailStage, SendFailure } from './send-failure';

export const SEND_FAIL_REASONS = [
    // media upload (nothing reached WhatsApp's message server)
    'upload_http_status',   // the media server answered with a non-2xx status
    'upload_timeout',
    'upload_network',
    'upload_too_large',     // 413
    'upload_invalid_media', // 415
    'upload_unauthorized',  // 401
    'upload_forbidden',     // 403
    'upload_throttled',     // 507
    'upload_not_found',     // 404
    'upload_cancelled',
    'upload_exception',     // any other upload throw
    // message send
    'server_nack',          // the server acked the message with an error code
    'server_status_error',  // the phone/server answered a non-200 status
    'payload_too_large',    // the stanza exceeded the buffer limit before it was sent
    'invalid_message',      // the outgoing protobuf failed validation before it was sent
    'send_exception',       // the send job threw anything else
] as const;

export type SendFailReason = (typeof SEND_FAIL_REASONS)[number];

const SEND_FAIL_REASON_SET: ReadonlySet<string> = new Set(SEND_FAIL_REASONS);

export function isSendFailReason(value: unknown): value is SendFailReason {
    return typeof value === 'string' && SEND_FAIL_REASON_SET.has(value);
}

/** One observed cause. Only the field that belongs to the reason is read. */
export interface SendFailCause {
    reason: SendFailReason;
    /** `server_nack`: the server's ack `error` attribute. */
    nackCode?: number;
    /** `upload_*` / `server_status_error`: the HTTP-like status. */
    httpStatus?: number;
    /** `send_exception` / `upload_exception`: `Error.name` only. */
    errorClass?: string;
}

interface ReasonShape {
    stage: SendFailStage;
    code: EngineErrorCode;
    onWire: boolean | 'unknown';
}

function shapeOf(reason: SendFailReason): ReasonShape {
    switch (reason) {
        case 'upload_http_status':
        case 'upload_timeout':
        case 'upload_network':
        case 'upload_not_found':
        case 'upload_exception':
            return { stage: 'upload', code: 'engine_error', onWire: false };
        case 'upload_too_large':
        case 'upload_invalid_media':
            return { stage: 'upload', code: 'invalid_input', onWire: false };
        case 'upload_unauthorized':
        case 'upload_forbidden':
            return { stage: 'upload', code: 'refused', onWire: false };
        case 'upload_throttled':
            return { stage: 'upload', code: 'rate_limited', onWire: false };
        case 'upload_cancelled':
            return { stage: 'cancelled', code: 'engine_error', onWire: false };
        case 'server_nack':
            return { stage: 'ack', code: 'refused', onWire: true };
        case 'server_status_error':
            return { stage: 'send', code: 'ambiguous', onWire: 'unknown' };
        case 'payload_too_large':
        case 'invalid_message':
            return { stage: 'prep', code: 'invalid_input', onWire: false };
        case 'send_exception':
            return { stage: 'send', code: 'ambiguous', onWire: 'unknown' };
        default: {
            const unhandled: never = reason;
            throw new Error(`unhandled send fail reason: ${String(unhandled)}`);
        }
    }
}

const MAX_CODE = 9999;

function smallInt(v: unknown): number | undefined {
    return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= MAX_CODE ? v : undefined;
}

/** `Error.name`-shaped only (`TypeError`, `SignalError`); a bare word like a name is dropped. */
function safeErrorClass(v: unknown): string | undefined {
    return typeof v === 'string' && /^([A-Z][A-Za-z]{0,30})?(Error|Exception)$/.test(v) ? v : undefined;
}

/** The short native token (`nack_475`, `upload_http_503`, `send_exception_TypeError`). */
export function sendFailCauseToken(cause: SendFailCause): string {
    const { reason } = cause;
    switch (reason) {
        case 'server_nack': {
            const code = smallInt(cause.nackCode);
            return code === undefined ? 'nack' : `nack_${code}`;
        }
        case 'upload_http_status': {
            const status = smallInt(cause.httpStatus);
            return status === undefined ? reason : `upload_http_${status}`;
        }
        case 'server_status_error': {
            const status = smallInt(cause.httpStatus);
            return status === undefined ? reason : `server_status_${status}`;
        }
        case 'send_exception':
        case 'upload_exception': {
            const cls = safeErrorClass(cause.errorClass);
            return cls === undefined ? reason : `${reason}_${cls}`;
        }
        case 'upload_timeout':
        case 'upload_network':
        case 'upload_too_large':
        case 'upload_invalid_media':
        case 'upload_unauthorized':
        case 'upload_forbidden':
        case 'upload_throttled':
        case 'upload_not_found':
        case 'upload_cancelled':
        case 'payload_too_large':
        case 'invalid_message':
            return reason;
        default: {
            const unhandled: never = reason;
            return unhandled;
        }
    }
}

/** The number a cause carries: the nack code for a nack, the HTTP-like status otherwise. */
function causeNumber(cause: SendFailCause): number | undefined {
    return smallInt(cause.reason === 'server_nack' ? cause.nackCode : cause.httpStatus);
}

/**
 * The structured record for one cause (sanitized: only small ints and class names survive).
 * `nativeCode` is the cause's number ('475', '415') when it has one, else the reason name:
 * both forms pass `sanitizeFailure`'s native-code rule, so `failureToMetadata` keeps them.
 */
export function sendFailCauseToFailure(cause: SendFailCause, engine: SendFailEngine): SendFailure {
    const errorClass = safeErrorClass(cause.errorClass);
    const num = causeNumber(cause);
    return {
        ...shapeOf(cause.reason),
        engine,
        nativeCode: num !== undefined ? String(num) : cause.reason,
        ...(errorClass !== undefined ? { errorClass } : {}),
    };
}
